import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"
import { CircleIcon } from "@radix-ui/react-icons"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

type OptionValue = {
  label: string
  value: number
} | null

type QuestionOptions = {
  hyper: OptionValue
  hypo: OptionValue
  dyst: OptionValue
  anergie: OptionValue
  synd: OptionValue
}

type Question = {
  id: string
  label: string
  type?: "matrix" | "text_group" | "checkbox_group" | "textarea"
  options?: QuestionOptions | string[]
  fields?: string[]
}

type Section = {
  id: string
  title: string
  questions: Question[]
}

type Category = {
  id: string
  title: string
  sections: Section[]
}

interface ShowProps {
  patient: {
    id: number
    first_name: string
    last_name: string
  }
  consultation: {
    id: string
    consultation_date: string
    answers: Record<string, string[]>
    notes: string
    computed_scores: Record<string, number>
    status: string
  }
  recommendations?: {
    scores: Record<string, number>
    profile: {
      type: string
      score: number
      age?: number
      details: {
        label: string
        products: string[]
        primary: string
        elements_needed: string[]
      }
    }
    evolution?: {
      label: string
      elements_needed: string[]
      score: number
    }
    syndrome?: {
      label: string
      elements_needed: string[]
      details: any[]
    }
    global_needs?: string[]
    products: {
      ref: string
      name: string
      elements: string[]
      reason: string
    }[]
  }
  categories: Category[]
  oligoElements: Record<string, { name: string; keywords: string }>
}

const COLUMNS = ["hyper", "hypo", "dyst", "anergie", "synd"] as const

export default function Show({ patient, consultation, categories, recommendations, oligoElements }: ShowProps) {
  const [notes, setNotes] = useState(consultation.notes || "")
  const [isSaving, setIsSaving] = useState(false)

  const handleSaveNotes = () => {
    setIsSaving(true)
    router.put(`/patients/${patient.id}/consultations/${consultation.id}`, {
      ...consultation,
      notes: notes
    }, {
      preserveScroll: true,
      onFinish: () => setIsSaving(false)
    })
  }

  return (
    <div className="container mx-auto py-8 max-w-5xl">
      <Head title={`Consultation - ${patient.first_name} ${patient.last_name}`} />
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Consultation du {consultation.consultation_date}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-muted-foreground">
              Patient: {patient.first_name} {patient.last_name}
              {recommendations && recommendations.profile?.age !== undefined && (
                <span className="text-muted-foreground text-sm font-normal ml-2">
                  ({recommendations.profile.age} ans)
                </span>
              )}
            </p>
            {consultation.status === "draft" && (
              <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                Brouillon
              </Badge>
            )}
          </div>
        </div>
        <Button variant="outline" asChild>
          <Link href={`/patients/${patient.id}`}>Retour au patient</Link>
        </Button>
      </div>

      <div className="grid gap-6 mb-8">
        {/* Recommendations Section */}
        {recommendations && (
          <Card className="border-primary/50 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 -mb-4">
                <span className="text-primary text-xl">Résultats</span>

              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-4 flex-wrap">
                {Object.entries(consultation.computed_scores).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="capitalize font-medium">{key}:</span>
                    <Badge variant="secondary" className="text-lg bg-white">
                      {value}
                    </Badge>
                  </div>
                ))}
              </div>

              {/* Profile */}
              {recommendations.profile && recommendations.profile.type && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Terrain (Diathèse)</h3>
                  <div className="bg-background p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-xl">{recommendations.profile.details.label}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <TooltipProvider>
                        {recommendations.profile.details.elements_needed?.map((el) => {
                          const info = oligoElements[el]
                          return (
                            <Tooltip key={el}>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="bg-white cursor-help">{el}</Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="font-bold">{info?.name || el}</p>
                                {info?.keywords && <p className="text-xs text-muted-foreground mt-1">{info.keywords}</p>}
                              </TooltipContent>
                            </Tooltip>
                          )
                        })}
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              )}

              {/* Evolution */}
              {recommendations.evolution && recommendations.evolution.label && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Évolution Générale</h3>
                  <div className="bg-background p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-xl">{recommendations.evolution.label}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <TooltipProvider>
                        {recommendations.evolution.elements_needed.map((el) => {
                          const info = oligoElements[el]
                          return (
                            <Tooltip key={el}>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="bg-white cursor-help">{el}</Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="font-bold">{info?.name || el}</p>
                                {info?.keywords && <p className="text-xs text-muted-foreground mt-1">{info.keywords}</p>}
                              </TooltipContent>
                            </Tooltip>
                          )
                        })}
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              )}

              {/* Syndrome */}
              {recommendations.syndrome && recommendations.syndrome.label && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Désadaptation (Syndrome)</h3>
                  <div className="bg-background p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-xl">{recommendations.syndrome.label}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <TooltipProvider>
                        {recommendations.syndrome.elements_needed.map(el => {
                          const info = oligoElements[el]
                          return (
                            <Tooltip key={el}>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="bg-white cursor-help">{el}</Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="font-bold">{info?.name || el}</p>
                                {info?.keywords && <p className="text-xs text-muted-foreground mt-1">{info.keywords}</p>}
                              </TooltipContent>
                            </Tooltip>
                          )
                        })}
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              )}

              {/* Global Needs Recap */}
              {recommendations.global_needs && recommendations.global_needs.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Bilan des besoins</h3>
                  <div className="bg-background p-4 rounded-lg border">
                    <div className="flex flex-wrap gap-2">
                      <TooltipProvider>
                        {recommendations.global_needs.map(el => {
                          const info = oligoElements[el]
                          return (
                            <Tooltip key={el}>
                              <TooltipTrigger asChild>
                                <Badge variant="default" className="cursor-help text-base px-3 py-1">{el}</Badge>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="font-bold">{info?.name || el}</p>
                                {info?.keywords && <p className="text-xs text-muted-foreground mt-1">{info.keywords}</p>}
                              </TooltipContent>
                            </Tooltip>
                          )
                        })}
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              )}

              {/* Products */}
              {recommendations.products && recommendations.products.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">Produits Conseillés</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {recommendations.products.map((product) => (
                      <div key={product.ref} className="bg-background p-4 rounded-lg border flex flex-col justify-between">
                        <div>
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-bold">{product.name}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2 whitespace-pre-line">{product.reason}</p>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          <TooltipProvider>
                            {product.elements.map(el => {
                              const info = oligoElements[el]
                              return (
                                <Tooltip key={el}>
                                  <TooltipTrigger asChild>
                                    <Badge variant="secondary" className="text-xs cursor-help">{el}</Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="font-bold">{info?.name || el}</p>
                                    {info?.keywords && <p className="text-xs text-muted-foreground mt-1">{info.keywords}</p>}
                                  </TooltipContent>
                                </Tooltip>
                              )
                            })}
                          </TooltipProvider>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Protocole recommandé</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Input 
                value={notes} 
                onChange={e => setNotes(e.target.value)} 
                placeholder="Saisir le protocole..."
                className="flex-1"
              />
              <Button onClick={handleSaveNotes} disabled={isSaving}>
                {isSaving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-12">
          {categories.map((category) => {
            // Check if category has any answered questions
            const hasAnswersInCategory = category.sections.some((section) =>
              section.questions.some(
                (q) =>
                  consultation.answers[q.id] &&
                  consultation.answers[q.id].length > 0
              )
            )

            if (!hasAnswersInCategory) return null

            return (
              <div key={category.id}>
                <h2 className="text-2xl font-semibold mb-6 border-b pb-2">
                  {category.title}
                </h2>
                <div className="space-y-6">
                  {category.sections
                    .map((section) => {
                      const answeredQuestions = section.questions.filter(
                        (q) =>
                          consultation.answers[q.id] &&
                          consultation.answers[q.id].length > 0
                      )

                      if (answeredQuestions.length === 0) return null

                      // Group answered questions into chunks
                      const chunks: { type: "matrix" | "other"; questions: Question[] }[] = []
                      let currentChunk: { type: "matrix" | "other"; questions: Question[] } | null = null

                      answeredQuestions.forEach((q) => {
                        const isMatrix = !q.type || q.type === "matrix"
                        const type = isMatrix ? "matrix" : "other"

                        if (!currentChunk || currentChunk.type !== type) {
                          currentChunk = { type, questions: [] }
                          chunks.push(currentChunk)
                        }
                        currentChunk.questions.push(q)
                      })

                      return (
                        <Card key={section.id}>
                          <CardHeader className="bg-muted/50 pb-4">
                            <CardTitle>{section.title}</CardTitle>
                          </CardHeader>
                          <CardContent className="p-0">
                            {chunks.map((chunk, chunkIndex) => (
                              <div key={chunkIndex} className={cn(chunk.type === "other" && "p-6")}>
                                {chunk.type === "matrix" ? (
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="w-1/3">Symptôme</TableHead>
                                        <TableHead className="text-center">Hyper</TableHead>
                                        <TableHead className="text-center">Hypo</TableHead>
                                        <TableHead className="text-center">Dyst</TableHead>
                                        <TableHead className="text-center">Anergie</TableHead>
                                        <TableHead className="text-center">Synd</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {chunk.questions.map((question) => {
                                        const answers = consultation.answers[question.id] || []

                                        return (
                                          <TableRow key={question.id}>
                                            <TableCell className="font-medium">
                                              {question.label}
                                            </TableCell>
                                            {COLUMNS.map((col) => {
                                              const options = question.options as QuestionOptions
                                              const option = options?.[col]
                                              const isActive = answers.includes(col)

                                              return (
                                                <TableCell
                                                  key={col}
                                                  className="text-center p-1"
                                                >
                                                  {option ? (
                                                    <div className="w-12 h-10 mx-auto flex items-center justify-center">
                                                      {isActive ? (
                                                        <svg
                                                          width="15"
                                                          height="15"
                                                          viewBox="0 0 15 15"
                                                          fill="none"
                                                          xmlns="http://www.w3.org/2000/svg"
                                                          className={cn(option.value === 2 ? "text-red-500" : "text-primary")}
                                                        >
                                                          <circle cx="7.5" cy="7.5" r="7" fill="currentColor" />
                                                        </svg>
                                                      ) : (
                                                        <CircleIcon className="text-muted-foreground" />
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <div className="w-12 h-10 mx-auto" />
                                                  )}
                                                </TableCell>
                                              )
                                            })}
                                          </TableRow>
                                        )
                                      })}
                                    </TableBody>
                                  </Table>
                                ) : (
                                  <div className="space-y-6">
                                    {chunk.questions.map((question) => {
                                      const answers = consultation.answers[question.id] || []
                                      
                                      return (
                                        <div key={question.id} className="space-y-2">
                                          <h4 className="font-semibold">{question.label}</h4>
                                          
                                          {question.type === "text_group" && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                              {answers.map((answer, idx) => {
                                                const [field, value] = answer.split(": ")
                                                return (
                                                  <div key={idx} className="bg-muted/30 p-2 rounded-md">
                                                    <span className="text-sm text-muted-foreground block">{field}</span>
                                                    <span>{value}</span>
                                                  </div>
                                                )
                                              })}
                                            </div>
                                          )}

                                          {question.type === "checkbox_group" && (
                                            <div className="flex flex-wrap gap-2">
                                              {answers.map((answer) => (
                                                <Badge key={answer} variant="secondary">
                                                  {answer}
                                                </Badge>
                                              ))}
                                            </div>
                                          )}

                                          {question.type === "textarea" && (
                                            <div className="bg-muted/30 p-4 rounded-md whitespace-pre-wrap">
                                              {answers[0]}
                                            </div>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )
                    })
                    .filter(Boolean)}
                </div>
              </div>
            )
          })}
        </div>
    </div>
  )
}
